import * as React from "react";
import { InputProps } from "../../interfaces/props/InputProps";
import { TooltipHost, Stack, IStackTokens, TagPicker, ITag, Label, Spinner, SpinnerSize, FontIcon } from "@fluentui/react";
import { DatasourceService } from "../../services/DatasourceService";
import { connect } from "react-redux";
import { RootState } from "../../redux";
import { DataSourcePanel } from "./datasourcePanel";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { useClientTypeContext } from "../hooks/useClientTypeContext";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { IDatasourceQueryDto } from "../../interfaces/IDatasourceQueryDto";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { filterSuggestedTagList, sortTerms } from "../helpers/tagHelper";

const stackTokens: IStackTokens = {
    childrenGap: 5
};

const getTextFromItem = (item: ITag) => item.name;

const getValueAsArray = (value: any): any[] => {
    return Array.isArray(value)
        ? value
        : (value
            ? (value as string)?.split(",")?.filter(x => x)?.map(x => x.trim()) ?? []
            : []);
}

const mapStateToProps = (state: RootState) => {
    return {
        datasourceItems: state.tenantInfo.datasources,
        currentDirectory: state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null
    };
};

interface DatasourceTagPickerProps extends InputProps, ReduxProps { }

type ReduxProps = ReturnType<typeof mapStateToProps>;

export const UnconnectedDatasourceTagPicker: React.FunctionComponent<DatasourceTagPickerProps> = ({
    displayName,
    isRequired,
    inputValue,
    inputValidator,
    updateInputValidationStatus,
    error,
    datasourceItems,
    currentDirectory,
    containerDisplayNames
}: DatasourceTagPickerProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
    const [boundedField, setBoundedField] = React.useState<string>("");
    const [boundName] = React.useState<string>(inputValue?.xml?.ContentTypeInputName ?? displayName);
    const dataSourceService = new DatasourceService();
    const dataSourceHelper = new DatasourceHelper(dataSourceService);
    const clientTypes = useClientTypeContext(containerDisplayNames?.siteId ?? currentDirectory?.siteId);
    // useMemo, NOT a one-shot useState initializer: the client-type context can settle after
    // mount, and a captured verdict would silently ignore it. getInputDataSource returns the
    // same object while the verdict is unchanged, so downstream [dataSourceItem] deps are stable.
    const dataSourceItem = React.useMemo(
        () => DatasourceHelper.getInputDataSource(datasourceItems, boundName, [containerDisplayNames?.siteDisplayName ?? currentDirectory?.siteDisplayName ?? ""], [containerDisplayNames?.libraryDisplayName ?? currentDirectory?.DocumentLibrary ?? currentDirectory?.sharepointList ?? ""], clientTypes),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clientTypes]
    );
    const [selectedTags, setSelectedTags] = React.useState<ITag[]>([]);
    const [staticTags, setStaticTags] = React.useState<ITag[] | undefined>();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const cachedSuggestions = React.useRef<ITag[] | undefined>(undefined);
    // Bumped whenever the resolved datasource changes; async work captures it at start and
    // discards its writes if outdated - a stale run resuming after a datasource change must
    // not stomp the new datasource's state or re-poison the suggestion cache.
    const datasourceEpochRef = React.useRef<number>(0);
    const isFirstRun = React.useRef(true);
    React.useEffect(() => {
        if (Array.isArray(inputValue?.updatedDatasourceOptions) && dataSourceItem?.boundFields?.some(item => item.isMaster)) {
            try {
                const allowedOptions: ITag[] = inputValue.updatedDatasourceOptions.map(option => ({ key: option, name: option }));
                setStaticTags(allowedOptions);
                if (selectedTags?.length > 0) {
                    onChangeHandle(selectedTags.filter(x => allowedOptions.some(a => a.key === x.key)));
                }
            } catch (e) {
                logger(e, "datasourceTagPicker.tsx (updatedDatasourceOptions)");
            }
        }
    }, [inputValue?.updatedDatasourceOptions]);

    React.useEffect(() => {
        if (!isFirstRun.current) {
            try {
                setStaticTags(undefined);
                cachedSuggestions.current = undefined;
            } catch (e) {
                logger(e, "datasourceTagPicker.tsx (resetDatasourceOptionsToggle)");
            }
        }
    }, [inputValue?.resetDatasourceOptionsToggle]);

    React.useEffect(() => {
        // A verdict-change re-fire means the datasource itself changed: suggestions cached
        // from the previous datasource's "*" query and master-pushed static tags belong to
        // it, not to the new one - serve neither (same reset the toggle effect performs).
        // The epoch bump also invalidates any suggestion query still in flight, so a late
        // landing cannot re-poison the cache after this clear (see filterSuggestedTags).
        const epoch: number = ++datasourceEpochRef.current;
        if (!isFirstRun.current) {
            cachedSuggestions.current = undefined;
            setStaticTags(undefined);
        }
        const setupAsyncWrapper = async () => {
            setIsLoading(true);
            try {
                const result = await dataSourceHelper.getSearchStructure(dataSourceItem);
                if (epoch !== datasourceEpochRef.current) {
                    return;
                }
                if (Array.isArray(result)) setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
                const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, boundName);
                setBoundedField(boundedFieldName ?? "");
            } catch (error) {
                logger(error, "datasourceTagPicker.tsx");
            }
            if (epoch === datasourceEpochRef.current) {
                setIsLoading(false);
            }
        };
        setupAsyncWrapper();
        // Keyed on the resolved datasource so a client-type verdict that settles after mount
        // reloads the structure instead of being silently ignored.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSourceItem]);

    React.useEffect(() => {
        try {
            const selectedValues: string[] = getValueAsArray(inputValue?.value);
            setSelectedTags(selectedValues.map(x => ({ key: x, name: x })));

            if (isFirstRun.current) {
                isFirstRun.current = false;
                return;
            }

            updateSearch(selectedValues.length === 1 ? selectedValues[0] : "");
            updateInputValidationStatus(inputValidator.validate(selectedValues.join(", ")));
        } catch (e) {
            logger(e, "datasourceTagPicker.tsx (useEffect 2)");
        }
    }, [inputValue?.updatedFromDataSourceToggle, inputValue?.value]);

    const filterSuggestedTags = React.useCallback(async (filterText: string, tagList: ITag[]): Promise<ITag[]> => {
        const getAllSuggestions: boolean = filterText === "*";
        // Captured at query start: a datasource change while the Search is in flight makes
        // this query's rows the OLD datasource's - never cache them (the cleared cache must
        // stay cleared).
        const epochAtQueryStart: number = datasourceEpochRef.current;
        let suggestedTags: ITag[] = [];
        if (filterText?.trim()) {
            if (Array.isArray(staticTags)) {
                return filterSuggestedTagList(staticTags, filterText, tagList, getAllSuggestions);
            }
            else {
                try {
                    let result: any[];
                    if (getAllSuggestions && Array.isArray(cachedSuggestions.current) && cachedSuggestions.current.length > 0) {
                        result = cachedSuggestions.current;
                    }
                    else {
                        const request: IDatasourceQueryDto = {
                            id: dataSourceItem.id,
                            name: dataSourceItem.name,
                            tenantId: dataSourceItem.tenantId,
                            typeId: dataSourceItem.type.id,
                            typeName: dataSourceItem.type.name,
                            queryParams: !getAllSuggestions
                                ? [{ name: boundedField, value: filterText.trim() }]
                                : null,
                            returnOnlyQueriedProperties: true,
                            allowCache: dataSourceItem.allowCache
                        };
                        result = await dataSourceService.Search(request);
                    }

                    if (epochAtQueryStart !== datasourceEpochRef.current) {
                        // Datasource changed while the query was in flight: these are the
                        // OLD datasource's rows - neither display nor cache them.
                        return [];
                    }

                    if (result.length > 0) {
                        const boundFieldProperty: string = Object.keys(result[0]).find(key => key.toLowerCase().trim() === boundedField.toLowerCase().trim());
                        if (boundFieldProperty) {
                            if (getAllSuggestions
                                && epochAtQueryStart === datasourceEpochRef.current
                                && (!Array.isArray(cachedSuggestions.current) || cachedSuggestions.current.length === 0)) {
                                cachedSuggestions.current = result;
                            }

                            suggestedTags = filterSuggestedTagList(
                                result.map<ITag>(resultRow => {
                                    const propertyValue = resultRow[boundFieldProperty]?.trim();
                                    return {
                                        name: propertyValue,
                                        key: propertyValue
                                    }
                                }),
                                filterText, tagList, getAllSuggestions);
                        }
                    }
                } catch (error) {
                    logger(error, "datasourceTagPicker.tsx (filterSuggestedTags)");
                }
            }
        }
        return sortTerms(filterText, suggestedTags);
    }, [dataSourceService, dataSourceItem, boundedField, staticTags]);

    const onEmptyResolveSuggestions = async (selectedItems?: ITag[]): Promise<ITag[]> => {
        return await filterSuggestedTags("*", selectedItems);
    }

    const closePanel = () => {
        setIsOpen(false);
    };

    const updateInputValue = (newValue: string) => {
        updateInputValidationStatus(inputValidator.validate([newValue]));
        updateSearch(newValue);
    };

    const updateSearchInputs = (index: number, value: string) => {
        DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
    };

    const openPanel = async () => {
        setIsOpen(true);
    };

    const updateSearch = (newValue: string) => {
        const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
        updateSearchInputs(index, newValue);
    };

    const onChangeHandle = (tags: ITag[]): void => {
        setSelectedTags(tags);
        updateSearch(tags?.length === 1 ? tags[0].key as string : "");
        updateInputValidationStatus(inputValidator.validate(tags.map(tag => tag.key).join(', ')));
    };

    return (
        <>
            <Stack horizontal verticalAlign="start" tokens={stackTokens}>
                <Stack.Item grow={1}>
                    <Label required={isRequired()}>{displayName}</Label>
                    <TagPicker
                        disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
                        className="xb-sp-input"
                        removeButtonAriaLabel="Remove"
                        selectedItems={selectedTags}
                        onResolveSuggestions={filterSuggestedTags}
                        onEmptyResolveSuggestions={onEmptyResolveSuggestions}
                        resolveDelay={300}
                        getTextFromItem={getTextFromItem}
                        onChange={onChangeHandle}
                        inputProps={{
                            required: isRequired(),
                        }}
                    />
                </Stack.Item>
                {inputValue?.xml?.ReadOnly !== SharePointMetadataBooleans.true && <Stack.Item>
                    {isLoading
                        ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
                        : <div className="xb-search-icon-container">
                            <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                                <FontIcon iconName="Search" onClick={openPanel} />
                            </TooltipHost>
                        </div>}
                </Stack.Item>}
            </Stack>
            <DataSourcePanel
                displayName={dataSourceItem?.name}
                closePanel={closePanel}
                isOpen={isOpen}
                datasourceItem={dataSourceItem}
                datasourceService={dataSourceService}
                searchInputs={searchInputs}
                updateSearchInputs={updateSearchInputs}
                updateInputValue={updateInputValue}
                boundedField={boundedField}
            />
        </>
    );
};

export const DatasourceTagPicker = connect(mapStateToProps, null)(UnconnectedDatasourceTagPicker);
